import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { COLORS, SPACING } from '../styles/constants';
import { FaArrowLeft } from 'react-icons/fa';

const ServiceDetailPage = styled.div`
  color: ${COLORS.dark};
`;

const PageHeader = styled.section`
  background-color: ${COLORS.dark};
  color: ${COLORS.white};
  padding: ${SPACING.xxl} 0 ${SPACING.xxl};
  text-align: center;
  padding-top: calc(${SPACING.xxl} * 2);
  position: relative;
`;

const HeaderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${SPACING.xl};
  padding-top: ${SPACING.xl};
`;

const PageTitle = styled.h1`
  font-size: 2.8rem;
  margin-bottom: ${SPACING.lg};
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background-color: ${COLORS.primary};
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ServiceContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${SPACING.xxl} ${SPACING.md};
`;

const ServiceDescription = styled.div`
  font-size: 1.1rem;
  color: ${COLORS.darkGrey};
  max-width: 800px;
  margin: 0 auto ${SPACING.xl};
  line-height: 1.6;
  text-align: center;
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const FeatureBox = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 144, 192, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.2rem;
  color: ${COLORS.dark};
  margin-bottom: 15px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  color: ${COLORS.darkGrey};
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
  font-size: 0.95rem;
  line-height: 1.4;
  
  &:before {
    content: '•';
    color: ${COLORS.primary};
    position: absolute;
    left: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  margin-top: ${SPACING.lg};
  margin-bottom: ${SPACING.xl};
  color: ${COLORS.primary};
  text-decoration: none;
  font-weight: 600;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    color: #0077a8;
    transform: translateX(-5px);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const ServiceDetail = ({ service }) => {
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const handleBackClick = () => {
    navigate('/');
    setTimeout(() => {
      const servicesSection = document.getElementById('services');
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (!service) {
    return (
      <ServiceDetailPage>
        <PageHeader>
          <HeaderContainer>
            <PageTitle>Service Not Found</PageTitle>
          </HeaderContainer>
        </PageHeader>
        <ServiceContainer>
          <BackButton onClick={handleBackClick}>
            <FaArrowLeft /> Back to Services
          </BackButton>
          <p>The requested service could not be found.</p>
        </ServiceContainer>
      </ServiceDetailPage>
    );
  }

  return (
    <ServiceDetailPage>
      <PageHeader>
        <HeaderContainer>
          <PageTitle>{service.title}</PageTitle>
        </HeaderContainer>
      </PageHeader>
      
      <ServiceContainer>
        <BackButton onClick={handleBackClick}>
          <FaArrowLeft /> Back to Services
        </BackButton>
        
        <ServiceDescription dangerouslySetInnerHTML={{ __html: service.description }} />
        
        <FeaturesList>
          <FeatureBox>
            <FeatureTitle>Key Features</FeatureTitle>
            <FeatureList>
              {service.features.map((feature, idx) => (
                <FeatureItem 
                  key={idx} 
                  dangerouslySetInnerHTML={{ __html: feature }}
                />
              ))}
            </FeatureList>
          </FeatureBox>
          
          <FeatureBox>
            <FeatureTitle>Business Impact</FeatureTitle>
            <FeatureList>
              {service.impact.map((impact, idx) => (
                <FeatureItem 
                  key={idx} 
                  dangerouslySetInnerHTML={{ __html: impact }}
                />
              ))}
            </FeatureList>
          </FeatureBox>
        </FeaturesList>
      </ServiceContainer>
    </ServiceDetailPage>
  );
};

export default ServiceDetail; 